import { TextField, type TextFieldProps } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface HoursMinutesTextFieldProps
	extends Omit<TextFieldProps, "onChange" | "value"> {
	/** Value in decimal hours (e.g. 1.5 = 1:30, 150.75 = 150:45) */
	value: number | null | undefined;
	/** Called with decimal hours (e.g. 1.5) */
	onChange?: (value: number | null) => void;
}

/** Convert decimal hours to HH:MM string. e.g. 1.5 -> "01:30" */
export const toHHMM = (decimalHours: number | null | undefined): string => {
	if (decimalHours === null || decimalHours === undefined) return "";
	const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/** Convert decimal hours to human-readable duration. e.g. 25.5 -> "1d 1h 30m" */
export const toHumanDuration = (
	decimalHours: number | null | undefined,
): string => {
	if (decimalHours === null || decimalHours === undefined) return "";
	const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
	return formatMinutesAsDuration(totalMinutes);
};

// ── Helpers ──

/** Decompose total minutes into { days, hours, minutes } */
const decompose = (
	totalMinutes: number,
): { days: number; hours: number; minutes: number } => ({
	days: Math.floor(totalMinutes / (24 * 60)),
	hours: Math.floor((totalMinutes % (24 * 60)) / 60),
	minutes: totalMinutes % 60,
});

/** Format total minutes as "Xd Xh Xm" — days segment only when > 0 */
const formatMinutesAsDuration = (totalMinutes: number): string => {
	const { days, hours, minutes } = decompose(totalMinutes);
	const parts: string[] = [];
	if (days > 0) parts.push(`${days}d`);
	if (hours > 0 || days > 0) parts.push(`${hours}h`);
	parts.push(`${minutes}m`);
	return parts.join(" ");
};

/** Decimal hours → display parts. Days only when >= 24h */
const decimalToDisplay = (
	decimalHours: number | null | undefined,
): { days: string; hours: string; minutes: string; showDays: boolean } => {
	if (decimalHours === null || decimalHours === undefined)
		return { days: "", hours: "", minutes: "", showDays: false };
	const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
	const { days, hours, minutes } = decompose(totalMinutes);
	const showDays = days > 0;
	return {
		days: showDays ? String(days) : "",
		hours: String(hours),
		minutes: String(minutes).padStart(2, "0"),
		showDays,
	};
};

/** Combine display strings → decimal hours. Hours are uncapped during editing. */
const displayToDecimal = (
	days: string,
	hours: string,
	minutes: string,
): number | null => {
	const d = days === "" ? 0 : Number.parseInt(days, 10);
	const h = hours === "" ? NaN : Number.parseInt(hours, 10);
	const m = minutes === "" ? 0 : Number.parseInt(minutes, 10);

	if (Number.isNaN(d)) return null;
	if (Number.isNaN(h) && minutes === "" && days === "") return null;
	if (Number.isNaN(h)) return d * 24 + m / 60;

	return d * 24 + h + (Number.isNaN(m) ? 0 : m) / 60;
};

// ── Segment styles ──

const baseSegmentStyle: React.CSSProperties = {
	border: "none",
	outline: "none",
	background: "transparent",
	font: "inherit",
	color: "inherit",
	lineHeight: "1.4375em",
	textAlign: "right",
	padding: 0,
	height: "1.4375em",
};

/** Returns segment style with width based on content length (min 1ch for placeholder) */
const getSegmentStyle = (
	value: string,
	placeholder: string,
): React.CSSProperties => ({
	...baseSegmentStyle,
	width: `${Math.max(value.length, placeholder.length)}ch`,
});

// ── DurationSegments (custom inputComponent for MUI TextField) ──

const DurationSegments = React.forwardRef<
	HTMLDivElement,
	{
		showDays: boolean;
		daysDisplay: string;
		hoursDisplay: string;
		minutesDisplay: string;
		onDaysChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
		onHoursChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
		onMinutesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
		onDaysKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
		onHoursKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
		onMinutesKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
		onSegmentFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
		onSegmentBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
		daysRef: React.Ref<HTMLInputElement>;
		hoursRef: React.Ref<HTMLInputElement>;
		minutesRef: React.Ref<HTMLInputElement>;
		disabled?: boolean;
		readOnly?: boolean;
		label?: React.ReactNode;
		className?: string;
	}
>(function DurationSegments(props, ref) {
	const {
		showDays,
		daysDisplay,
		hoursDisplay,
		minutesDisplay,
		onDaysChange,
		onHoursChange,
		onMinutesChange,
		onDaysKeyDown,
		onHoursKeyDown,
		onMinutesKeyDown,
		onSegmentFocus,
		onSegmentBlur,
		daysRef,
		hoursRef,
		minutesRef,
		disabled,
		readOnly,
		label,
		className,
	} = props;

	return (
		<div
			ref={ref}
			className={className}
			style={{
				display: "flex",
				alignItems: "center",
				font: "inherit",
				color: "inherit",
			}}
		>
			{showDays && (
				<>
					<input
						ref={daysRef}
						value={daysDisplay}
						onChange={onDaysChange}
						onKeyDown={onDaysKeyDown}
						onFocus={onSegmentFocus}
						onBlur={onSegmentBlur}
						placeholder="0"
						disabled={disabled}
						readOnly={readOnly}
						inputMode="numeric"
						aria-label={`${label} days`}
						style={getSegmentStyle(daysDisplay, "0")}
					/>
					<span>d</span>
					{/* Spacer between d and h */}
					<span style={{ width: 8, flexShrink: 0 }} />
				</>
			)}
			<input
				ref={hoursRef}
				value={hoursDisplay}
				onChange={onHoursChange}
				onKeyDown={onHoursKeyDown}
				onFocus={onSegmentFocus}
				onBlur={onSegmentBlur}
				placeholder="00"
				disabled={disabled}
				readOnly={readOnly}
				inputMode="numeric"
				aria-label={`${label} hours`}
				style={getSegmentStyle(hoursDisplay, "00")}
			/>
			<span>h</span>
			{/* Spacer between h and m */}
			<span style={{ width: 8, flexShrink: 0 }} />
			<input
				ref={minutesRef}
				value={minutesDisplay}
				onChange={onMinutesChange}
				onKeyDown={onMinutesKeyDown}
				onFocus={onSegmentFocus}
				onBlur={onSegmentBlur}
				placeholder="00"
				disabled={disabled}
				readOnly={readOnly}
				inputMode="numeric"
				aria-label={`${label} minutes`}
				style={getSegmentStyle(minutesDisplay, "00")}
			/>
			<span>m</span>
		</div>
	);
});

// ── Main component ──

export const HoursMinutesTextField = React.forwardRef<
	HTMLDivElement,
	HoursMinutesTextFieldProps
>(
	(
		{
			value,
			onChange = () => {},
			label,
			error,
			helperText,
			disabled,
			...props
		},
		ref,
	) => {
		const isReadOnly = !!props["aria-readonly"];
		const daysRef = useRef<HTMLInputElement>(null);
		const hoursRef = useRef<HTMLInputElement>(null);
		const minutesRef = useRef<HTMLInputElement>(null);
		const [showDays, setShowDays] = useState(false);
		const [daysDisplay, setDaysDisplay] = useState("");
		const [hoursDisplay, setHoursDisplay] = useState("");
		const [minutesDisplay, setMinutesDisplay] = useState("");
		const isEditingRef = useRef(false);

		// Sync from external value prop (only when not actively editing)
		useEffect(() => {
			if (isEditingRef.current) return;
			const parts = decimalToDisplay(value);
			setShowDays(parts.showDays);
			setDaysDisplay(parts.days);
			setHoursDisplay(parts.hours);
			setMinutesDisplay(parts.minutes);
		}, [value]);

		const emitChange = useCallback(
			(d: string, h: string, m: string) => {
				onChange(displayToDecimal(d, h, m));
			},
			[onChange],
		);

		/** Normalize on blur: overflow hours into days, overflow minutes into hours */
		const normalize = useCallback(() => {
			const d = daysDisplay === "" ? 0 : Number.parseInt(daysDisplay, 10) || 0;
			const h =
				hoursDisplay === "" ? 0 : Number.parseInt(hoursDisplay, 10) || 0;
			const m =
				minutesDisplay === "" ? 0 : Number.parseInt(minutesDisplay, 10) || 0;

			const totalMinutes = d * 24 * 60 + h * 60 + m;

			if (totalMinutes === 0) {
				setShowDays(false);
				setDaysDisplay("");
				setHoursDisplay("");
				setMinutesDisplay("");
				return;
			}

			const parts = decompose(totalMinutes);
			const needsDays = parts.days > 0;

			setShowDays(needsDays);
			setDaysDisplay(needsDays ? String(parts.days) : "");
			setHoursDisplay(String(parts.hours));
			setMinutesDisplay(String(parts.minutes).padStart(2, "0"));
		}, [daysDisplay, hoursDisplay, minutesDisplay]);

		const handleDaysChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/[^0-9]/g, "");
				if (raw.length > 3) return;
				isEditingRef.current = true;
				setDaysDisplay(raw);
				emitChange(raw, hoursDisplay, minutesDisplay);
			},
			[emitChange, hoursDisplay, minutesDisplay],
		);

		const handleHoursChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/[^0-9]/g, "");
				if (raw.length > 3) return; // No cap — normalizes on blur
				isEditingRef.current = true;
				setHoursDisplay(raw);
				emitChange(daysDisplay, raw, minutesDisplay);
			},
			[emitChange, daysDisplay, minutesDisplay],
		);

		const handleMinutesChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/[^0-9]/g, "");
				if (raw.length > 3) return; // No cap — normalizes on blur
				isEditingRef.current = true;
				setMinutesDisplay(raw);
				emitChange(daysDisplay, hoursDisplay, raw);
			},
			[emitChange, daysDisplay, hoursDisplay],
		);

		/** Check if cursor is at the end of an input (or fully selected) */
		const isAtEnd = (input: HTMLInputElement) =>
			input.selectionStart === input.value.length ||
			(input.selectionStart === 0 && input.selectionEnd === input.value.length);

		/** Check if cursor is at the start of an input (or fully selected) */
		const isAtStart = (input: HTMLInputElement) => input.selectionStart === 0;

		const handleDaysKeyDown = useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (
					e.key === ":" ||
					e.key === " " ||
					(e.key === "Tab" && !e.shiftKey)
				) {
					e.preventDefault();
					hoursRef.current?.focus();
				}
				if (e.key === "ArrowRight" && isAtEnd(e.currentTarget)) {
					e.preventDefault();
					hoursRef.current?.focus();
				}
			},
			[],
		);

		const handleHoursKeyDown = useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (
					e.key === ":" ||
					e.key === " " ||
					(e.key === "Tab" && !e.shiftKey)
				) {
					e.preventDefault();
					minutesRef.current?.focus();
				}
				if (
					(e.key === "Backspace" && hoursDisplay === "") ||
					(e.key === "ArrowLeft" && isAtStart(e.currentTarget))
				) {
					if (showDays) {
						e.preventDefault();
						daysRef.current?.focus();
					}
				}
				if (e.key === "ArrowRight" && isAtEnd(e.currentTarget)) {
					e.preventDefault();
					minutesRef.current?.focus();
				}
			},
			[hoursDisplay, showDays],
		);

		const handleMinutesKeyDown = useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (
					(e.key === "Backspace" && minutesDisplay === "") ||
					(e.key === "ArrowLeft" && isAtStart(e.currentTarget))
				) {
					e.preventDefault();
					hoursRef.current?.focus();
				}
				// Shift+Tab from minutes → go to hours (not to previous form field)
				if (e.key === "Tab" && e.shiftKey) {
					e.preventDefault();
					hoursRef.current?.focus();
				}
			},
			[minutesDisplay],
		);

		const handleSegmentFocus = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				isEditingRef.current = true;
				setTimeout(() => e.target.select(), 0);
			},
			[],
		);

		const handleSegmentBlur = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				const related = e.relatedTarget as HTMLElement | null;
				if (
					related === daysRef.current ||
					related === hoursRef.current ||
					related === minutesRef.current
				) {
					return;
				}
				isEditingRef.current = false;
				normalize();
			},
			[normalize],
		);

		const dataTestId = label?.toString().toLowerCase().replaceAll(" ", "_");

		return (
			<TextField
				ref={ref}
				data-test-id={dataTestId}
				label={label}
				error={error}
				helperText={helperText}
				disabled={disabled}
				fullWidth
				size="small"
				variant="outlined"
				margin="none"
				InputLabelProps={{ shrink: true }}
				FormHelperTextProps={
					error ? undefined : { sx: { color: "primary.main" } }
				}
				sx={{
					"& .MuiInputBase-input": {
						display: "flex",
						alignItems: "center",
					},
				}}
				InputProps={{
					readOnly: isReadOnly,
					inputComponent: DurationSegments as any,
					inputProps: {
						showDays,
						daysDisplay,
						hoursDisplay,
						minutesDisplay,
						onDaysChange: handleDaysChange,
						onHoursChange: handleHoursChange,
						onMinutesChange: handleMinutesChange,
						onDaysKeyDown: handleDaysKeyDown,
						onHoursKeyDown: handleHoursKeyDown,
						onMinutesKeyDown: handleMinutesKeyDown,
						onSegmentFocus: handleSegmentFocus,
						onSegmentBlur: handleSegmentBlur,
						daysRef,
						hoursRef,
						minutesRef,
						disabled,
						readOnly: isReadOnly,
						label,
					},
				}}
				onClick={(e) => {
					if (disabled || isReadOnly) return;
					// Only focus hours as fallback when clicking empty space,
					// not when clicking directly on a segment input
					const target = e.target as HTMLElement;
					if (target.tagName === "INPUT") return;
					hoursRef.current?.focus();
				}}
			/>
		);
	},
);

HoursMinutesTextField.displayName = "HoursMinutesTextField";
